import { create } from 'zustand';
import type { ToastItem, ToastOptions } from './types';

const MAX_VISIBLE = 3;
const DEFAULT_DURATION = 5000;

interface ToastStoreState {
  items: ToastItem[];
  /** Odvozený cached snapshot viditelných toastů – stabilní reference */
  visibleItems: ToastItem[];
  addToast: (options: ToastOptions) => void;
  dismissToast: (id: string) => void;
  clearAll: () => void;
}

function promoteQueued(items: ToastItem[]): ToastItem[] {
  const visibleCount = items.filter((t) => t.status === 'visible').length;
  let slotsAvailable = MAX_VISIBLE - visibleCount;

  return items.map((item) => {
    if (item.status === 'queued' && slotsAvailable > 0) {
      slotsAvailable--;
      return { ...item, status: 'visible' as const };
    }
    return item;
  });
}

function deriveVisible(items: ToastItem[]): ToastItem[] {
  return items.filter((t) => t.status === 'visible');
}

export const useToastStoreInternal = create<ToastStoreState>((set) => ({
  items: [],
  visibleItems: [],

  addToast: (options) =>
    set((state) => {
      const newItem: ToastItem = {
        id: crypto.randomUUID(),
        severity: options.severity ?? 'info',
        title: options.title,
        description: options.description,
        action: options.action,
        duration: options.duration ?? DEFAULT_DURATION,
        status: 'queued',
      };
      const nextItems = promoteQueued([...state.items, newItem]);
      return { items: nextItems, visibleItems: deriveVisible(nextItems) };
    }),

  dismissToast: (id) =>
    set((state) => {
      const updated = state.items.map((t) =>
        t.id === id ? { ...t, status: 'dismissed' as const } : t
      );
      // Odstraň dismissed a promuj čekající
      const active = updated.filter((t) => t.status !== 'dismissed');
      const nextItems = promoteQueued(active);
      return { items: nextItems, visibleItems: deriveVisible(nextItems) };
    }),

  clearAll: () => set({ items: [], visibleItems: [] }),
}));

/**
 * Dispatcher API pro toast – lze použít i mimo React komponenty.
 */
export function toast(options: ToastOptions) {
  useToastStoreInternal.getState().addToast(options);
}

/** Visible toasty pro render – stabilní reference díky pre-computed snapshot */
export function useVisibleToasts() {
  return useToastStoreInternal((state) => state.visibleItems);
}

/** Dismiss akce */
export function useDismissToast() {
  return useToastStoreInternal((state) => state.dismissToast);
}
